import React from "react";
import { Grid, Stack, InputAdornment } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";

import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PercentIcon from "@mui/icons-material/Percent";

// Actions
import { createTaxCode, updateTaxCode } from "../redux/actions/taxCodeActions";

import { cleanFormData } from "../helpers/formHelpers";

const taxCodeSchema = Yup.object({
  code: Yup.string()
    .required("Tax code is required (e.g., A, B, VAT)")
    .max(10, "Code is too long"),
  name: Yup.string().required("Display name is required (e.g., 16% VAT)"),
  taxRate: Yup.number()
    .required("Tax rate is required")
    .min(0, "Rate cannot be negative")
    .max(100, "Rate cannot exceed 100%"),
});

const CreateOrUpdateTaxCodeForm = ({
  taxCode = null,
  isEditing = false,
  loading = false,
}) => {
  const dispatch = useDispatch();

  const initialValues = {
    code: taxCode?.code || "",
    name: taxCode?.name || "",
    taxRate: taxCode?.taxRate || "",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={taxCodeSchema}
      onSubmit={(values) => {
        const cleanedValues = cleanFormData(values);

        if (isEditing) {
          dispatch(updateTaxCode({ id: taxCode.id, values: cleanedValues }));
        } else {
          dispatch(createTaxCode(cleanedValues));
        }
      }}
      enableReinitialize
    >
      {({ dirty, isValid }) => (
        <Form>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name="code"
                  label="Tax Code"
                  placeholder="e.g., B"
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormTextField
                  name="name"
                  label="Tax Name"
                  placeholder="e.g., 16% VAT"
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  name="taxRate"
                  label="Tax Rate (%)"
                  type="number"
                  placeholder="e.g., 16.00"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <PercentIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <SubmitButton
              title={isEditing ? "Save Changes" : "Create Tax Code"}
              loading={loading}
              disabled={!dirty || !isValid || loading}
              startIcon={
                isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
              }
              sx={{ mt: 1 }}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default CreateOrUpdateTaxCodeForm;
